import { DatedProductRule } from 'types/policy';
import {
  getDatedProductRulesError,
  normalizeDatedProductRules,
} from './PolicySettings.datedRules';

export const PRODUCT_RULES_VERSION = 1;
export const PRODUCT_CATALOG_FRESHNESS_HOURS = 24;
export const PRODUCT_TIMESTAMP_FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

export type ProductEligibilityCapability = {
  available: boolean;
  scopesReady: boolean;
  apiVersionReady: boolean;
  catalogSyncedAt?: string | null;
  finalSaleDefinitionReady: boolean;
  metadataCoverage: 'COMPLETE' | 'INCOMPLETE' | 'UNAVAILABLE';
};

export type ProductEligibilityCollection = {
  id: string;
  title: string;
  handle?: string | null;
};

export type ProductEligibilityTag = {
  id: string;
  title: string;
};

export type ProductEligibilitySettingsInput = {
  productRulesVersion?: number;
  productRulesEnabled?: boolean;
  enabledAt?: string | null;
  finalSaleEnforced?: boolean;
  excludedTags?: unknown;
  excludedCollections?: unknown;
  excludedSkus?: unknown;
  datedRules?: unknown;
  excludedCollectionLabels?: unknown;
};

export type NormalizedProductEligibilitySettings = {
  productRulesVersion?: number;
  productRulesEnabled: boolean;
  enabledAt?: string;
  finalSaleEnforced: boolean;
  excludedTags: string[];
  excludedCollections: string[];
  excludedSkus: string[];
  datedRules: DatedProductRule[];
  datedRulesError: string | null;
  excludedCollectionLabels: Record<string, string>;
  isActive: boolean;
  hasLegacySavedRules: boolean;
};

export const hasConfiguredProductEligibilityRule = ({
  finalSaleEnforced,
  excludedTags,
  excludedCollections,
  excludedSkus,
  datedRules = [],
}: Pick<
  NormalizedProductEligibilitySettings,
  'finalSaleEnforced' | 'excludedTags' | 'excludedCollections' | 'excludedSkus'
> & { datedRules?: DatedProductRule[] }) =>
  finalSaleEnforced ||
  excludedTags.length > 0 ||
  excludedCollections.length > 0 ||
  excludedSkus.length > 0 ||
  datedRules.some((rule) => rule.enabled);

const normalizeStrings = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  return Array.from(
    new Set(
      value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );
};

const normalizeSkus = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  const seen = new Set<string>();
  return value.reduce<string[]>((skus, item) => {
    if (typeof item !== 'string') return skus;
    const sku = item.trim();
    const normalizedSku = sku.toLocaleLowerCase('en-US');
    if (!sku || seen.has(normalizedSku)) return skus;

    seen.add(normalizedSku);
    skus.push(sku);
    return skus;
  }, []);
};

const normalizeLabelMap = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  return Object.entries(value as Record<string, unknown>).reduce<
    Record<string, string>
  >((labels, [id, label]) => {
    if (typeof label === 'string' && id.trim() && label.trim()) {
      labels[id.trim()] = label.trim();
    }
    return labels;
  }, {});
};

export const normalizeProductEligibilitySettings = (
  value: ProductEligibilitySettingsInput | null | undefined,
  now = new Date(),
): NormalizedProductEligibilitySettings => {
  const settings = value || {};
  const excludedTags = normalizeStrings(settings.excludedTags);
  const excludedCollections = normalizeStrings(settings.excludedCollections);
  const excludedSkus = normalizeSkus(settings.excludedSkus);
  const datedRules = normalizeDatedProductRules(settings.datedRules);
  const datedRulesError = getDatedProductRulesError(settings.datedRules);
  const excludedCollectionLabels = normalizeLabelMap(
    settings.excludedCollectionLabels,
  );
  const enabledAtValue =
    typeof settings.enabledAt === 'string' ? settings.enabledAt.trim() : '';
  const enabledAtTimestamp = Date.parse(enabledAtValue);
  const enabledAt =
    enabledAtValue &&
    Number.isFinite(enabledAtTimestamp) &&
    enabledAtTimestamp <= now.getTime() + PRODUCT_TIMESTAMP_FUTURE_TOLERANCE_MS
      ? enabledAtValue
      : undefined;
  const isVersioned = settings.productRulesVersion === PRODUCT_RULES_VERSION;
  const productRulesEnabled = Boolean(settings.productRulesEnabled);
  const finalSaleEnforced = Boolean(settings.finalSaleEnforced);
  const hasSavedRules = hasConfiguredProductEligibilityRule({
    finalSaleEnforced,
    excludedTags,
    excludedCollections,
    excludedSkus,
    datedRules,
  });

  return {
    productRulesVersion: settings.productRulesVersion,
    productRulesEnabled,
    enabledAt,
    finalSaleEnforced,
    excludedTags,
    excludedCollections,
    excludedSkus,
    datedRules,
    datedRulesError,
    excludedCollectionLabels,
    isActive:
      isVersioned &&
      productRulesEnabled &&
      Boolean(enabledAt) &&
      hasSavedRules &&
      !datedRulesError,
    hasLegacySavedRules: hasSavedRules && !isVersioned,
  };
};

export const getProductEligibilityFingerprint = (
  value: ProductEligibilitySettingsInput | null | undefined,
) => {
  const settings = normalizeProductEligibilitySettings(value);
  const labels = Object.entries(settings.excludedCollectionLabels).sort(
    ([left], [right]) => left.localeCompare(right),
  );

  return JSON.stringify({
    productRulesVersion: settings.productRulesVersion ?? null,
    productRulesEnabled: settings.productRulesEnabled,
    enabledAt: settings.enabledAt ?? null,
    finalSaleEnforced: settings.finalSaleEnforced,
    excludedTags: [...settings.excludedTags].sort(),
    excludedCollections: [...settings.excludedCollections].sort(),
    excludedSkus: settings.excludedSkus
      .map((sku) => sku.toLocaleLowerCase('en-US'))
      .sort(),
    excludedCollectionLabels: labels,
    ...(settings.datedRules.length ? { datedRules: settings.datedRules } : {}),
  });
};

export const getProductEligibilityCatalogState = (
  capability: ProductEligibilityCapability | null | undefined,
  now = new Date(),
  reportedStale = false,
) => {
  const syncedAt = capability?.catalogSyncedAt
    ? new Date(capability.catalogSyncedAt)
    : null;
  const hasValidSync = Boolean(
    syncedAt &&
      !Number.isNaN(syncedAt.getTime()) &&
      syncedAt.getTime() <=
        now.getTime() + PRODUCT_TIMESTAMP_FUTURE_TOLERANCE_MS,
  );
  const ageMs = hasValidSync
    ? Math.max(0, now.getTime() - (syncedAt as Date).getTime())
    : Number.POSITIVE_INFINITY;
  const stale =
    reportedStale ||
    !hasValidSync ||
    ageMs > PRODUCT_CATALOG_FRESHNESS_HOURS * 60 * 60 * 1000;
  const ready = Boolean(
    capability?.available &&
      capability.scopesReady &&
      capability.apiVersionReady &&
      !stale,
  );

  return {
    ready,
    stale,
    syncedAt: hasValidSync ? (syncedAt as Date) : null,
  };
};

const isShopifyCollectionId = (value: string) =>
  /^gid:\/\/shopify\/Collection\/\d+$/.test(value);

export const getProductEligibilityActivationState = ({
  catalogReady,
  finalSaleEnforced,
  finalSaleDefinitionReady,
  excludedTags = [],
  excludedCollections = [],
  excludedSkus = [],
  datedRules = [],
  knownCollectionIds = [],
  knownTagIds,
}: {
  catalogReady: boolean;
  finalSaleEnforced: boolean;
  finalSaleDefinitionReady: boolean;
  excludedTags?: string[];
  excludedCollections?: string[];
  excludedSkus?: string[];
  datedRules?: DatedProductRule[];
  knownCollectionIds?: string[];
  knownTagIds?: string[];
}) => {
  if (!catalogReady) {
    return {
      ready: false,
      reason: 'Sync a current Shopify catalog before activating.',
      legacyCollections: [] as string[],
    };
  }

  const datedRulesError = getDatedProductRulesError(datedRules);
  if (datedRulesError)
    return {
      ready: false,
      reason: datedRulesError,
      legacyCollections: [] as string[],
    };

  const knownIds = new Set(knownCollectionIds);
  const legacyCollections = excludedCollections.filter(
    (collection) =>
      !isShopifyCollectionId(collection) || !knownIds.has(collection),
  );

  if (legacyCollections.length > 0) {
    return {
      ready: false,
      reason:
        'Remove and reselect legacy collection exclusions before activating.',
      legacyCollections,
    };
  }

  const knownTags = knownTagIds ? new Set(knownTagIds) : null;
  const missingTags = knownTags
    ? excludedTags.filter((tag) => !knownTags.has(tag))
    : [];
  const datedTags = new Set(
    (knownTagIds || []).map((tag) => tag.toLocaleLowerCase('en-US')),
  );
  const missingDatedTags = knownTagIds
    ? datedRules.filter(
        (rule) =>
          rule.enabled &&
          rule.field === 'PRODUCT_TAG' &&
          !datedTags.has(rule.value.toLocaleLowerCase('en-US')),
      )
    : [];

  if (missingDatedTags.length > 0)
    return {
      ready: false,
      reason: 'Reselect missing product tags in product rules before activating.',
      legacyCollections,
    };

  if (missingTags.length > 0) {
    return {
      ready: false,
      reason:
        'Remove or reselect saved tag exclusions that are no longer in Shopify before activating.',
      legacyCollections,
      missingTags,
    };
  }

  if (finalSaleEnforced && !finalSaleDefinitionReady) {
    return {
      ready: false,
      reason:
        'Create the shipaid.final_sale metafield definition before activating final-sale enforcement.',
      legacyCollections,
    };
  }

  if (
    !finalSaleEnforced &&
    excludedTags.length === 0 &&
    excludedCollections.length === 0 &&
    excludedSkus.length === 0 &&
    !datedRules.some((rule) => rule.enabled)
  ) {
    return {
      ready: false,
      reason:
        'Choose at least one final-sale, tag, collection, or SKU rule before activating.',
      legacyCollections,
    };
  }

  return { ready: true, reason: null, legacyCollections };
};

export const splitProductExclusionValues = (
  values: Array<number | string> = [],
  collections: ProductEligibilityCollection[] = [],
) => {
  const collectionMap = new Map(
    collections.map((collection) => [collection.id, collection]),
  );
  const excludedTags: string[] = [];
  const excludedCollections: string[] = [];
  const excludedCollectionLabels: Record<string, string> = {};

  values.forEach((rawValue) => {
    if (typeof rawValue !== 'string') return;

    if (rawValue.startsWith('tag:')) {
      const tag = rawValue.slice('tag:'.length).trim();
      if (tag && !excludedTags.includes(tag)) excludedTags.push(tag);
      return;
    }

    if (rawValue.startsWith('collection:')) {
      const id = rawValue.slice('collection:'.length).trim();
      const collection = collectionMap.get(id);
      if (!collection || excludedCollections.includes(id)) return;
      excludedCollections.push(id);
      excludedCollectionLabels[id] = collection.title;
    }
  });

  return { excludedTags, excludedCollections, excludedCollectionLabels };
};

export const isStableShopifyCollectionId = isShopifyCollectionId;
